<template>
  <div class="hash-generator-page">
    <!-- Hero Section -->
    <section class="hero-section">
      <div class="container">
        <div class="row justify-content-center">
          <div class="col-lg-10">
            <h1 class="hero-title">Hash Generator</h1>
            <p class="hero-subtitle">
              Generate cryptographic hashes (MD5, SHA-1, SHA-256, SHA-512) for text or files. Perfect for checksums, data integrity, and security testing.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Tool Section -->
    <section class="tool-section">
      <div class="container">
        <div class="row justify-content-center">
          <div class="col-lg-10">
            <!-- Input Section -->
            <div class="input-card mb-4">
              <div class="card-header">
                <h3>Input</h3>
                <div class="card-actions">
                  <button class="btn-action" title="Clear" @click="clearInput">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    </svg>
                  </button>
                  <button class="btn-action" title="Paste" @click="pasteFromClipboard">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                      <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
                    </svg>
                  </button>
                </div>
              </div>
              <textarea
                v-model="input"
                class="io-textarea"
                placeholder="Enter text to hash..."
                @input="generateHashes"
              />
              <div class="char-count">{{ input.length }} characters</div>
              
              <!-- File Upload -->
              <div class="file-upload-section mt-3">
                <label class="file-upload-label">
                  <input type="file" class="file-input" @change="handleFileUpload">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                  Or upload file to hash
                </label>
                <div v-if="fileName" class="file-name">{{ fileName }}</div>
              </div>
            </div>

            <!-- Hashes Output -->
            <div class="hashes-container">
              <div
                v-for="hash in hashes"
                :key="hash.algorithm"
                class="hash-card"
                :class="{ copied: hash.copied }"
                @click="copyHash(hash)"
              >
                <div class="hash-header">
                  <div class="hash-title">
                    <span class="hash-algo">{{ hash.algorithm }}</span>
                    <span class="hash-badge" :class="`badge-${hash.strength}`">{{ hash.strength }}</span>
                  </div>
                  <button class="btn-icon" title="Copy" @click.stop="copyHash(hash)">
                    <svg v-if="!hash.copied" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                    </svg>
                    <svg v-else xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </button>
                </div>
                <code class="hash-value">{{ hash.value || 'Hash will appear here...' }}</code>
                <div v-if="hash.copied" class="copied-feedback">Copied!</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Info Section -->
    <section class="info-section">
      <div class="container">
        <div class="row justify-content-center">
          <div class="col-lg-10">
            <div class="info-card">
              <h3>About Hash Functions</h3>
              <p>
                Cryptographic hash functions convert input data of any size into a fixed-size string of characters. 
                They are one-way functions, meaning you cannot reverse the process to get the original input.
              </p>
              
              <h4>Supported Algorithms</h4>
              <ul>
                <li>
                  <strong>MD5</strong> - 128-bit hash. Fast but <span class="text-warning">deprecated</span> for security purposes. 
                  Use only for non-security checksums.
                </li>
                <li>
                  <strong>SHA-1</strong> - 160-bit hash. <span class="text-warning">Deprecated</span> for security. 
                  Still used in some legacy systems.
                </li>
                <li>
                  <strong>SHA-256</strong> - 256-bit hash from SHA-2 family. <span class="text-success">Strong</span> and widely used 
                  for security applications.
                </li>
                <li>
                  <strong>SHA-512</strong> - 512-bit hash from SHA-2 family. <span class="text-success">Very strong</span> and 
                  recommended for maximum security.
                </li>
              </ul>
              
              <h4>Common Use Cases</h4>
              <ul>
                <li><strong>File Integrity</strong> - Verify downloads haven't been corrupted</li>
                <li><strong>Password Storage</strong> - Store hashed passwords (with salt)</li>
                <li><strong>Data Deduplication</strong> - Identify duplicate files</li>
                <li><strong>Digital Signatures</strong> - Verify data authenticity</li>
                <li><strong>Git Commits</strong> - Git uses SHA-1 for commit hashes</li>
                <li><strong>Blockchain</strong> - Bitcoin uses SHA-256</li>
              </ul>
              
              <h4>Security Notes</h4>
              <p>
                <strong>⚠️ Important:</strong> MD5 and SHA-1 are cryptographically broken and should not be used for security purposes. 
                Use SHA-256 or SHA-512 for any security-related applications. Never use plain hashing for passwords - 
                always use proper password hashing algorithms like bcrypt, Argon2, or PBKDF2.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'

useHead({
  title: 'Hash Generator | Developer Tools',
  meta: [
    { name: 'description', content: 'Generate cryptographic hashes (MD5, SHA-1, SHA-256, SHA-512) for text or files. Perfect for checksums and data integrity verification.' }
  ]
})

interface Hash {
  algorithm: string
  value: string
  copied: boolean
  strength: 'deprecated' | 'strong' | 'very-strong'
}

const input = ref('')
const fileName = ref('')
const hashes = ref<Hash[]>([
  { algorithm: 'MD5', value: '', copied: false, strength: 'deprecated' },
  { algorithm: 'SHA-1', value: '', copied: false, strength: 'deprecated' },
  { algorithm: 'SHA-256', value: '', copied: false, strength: 'strong' },
  { algorithm: 'SHA-512', value: '', copied: false, strength: 'very-strong' }
])

const generateHashes = async () => {
  if (!input.value) {
    hashes.value.forEach(h => h.value = '')
    return
  }

  const encoder = new TextEncoder()
  const data = encoder.encode(input.value)

  try {
    // MD5 (using a simple implementation since Web Crypto doesn't support it)
    hashes.value[0].value = await generateMD5(input.value)
    
    // SHA-1
    const sha1Buffer = await crypto.subtle.digest('SHA-1', data)
    hashes.value[1].value = bufferToHex(sha1Buffer)
    
    // SHA-256
    const sha256Buffer = await crypto.subtle.digest('SHA-256', data)
    hashes.value[2].value = bufferToHex(sha256Buffer)
    
    // SHA-512
    const sha512Buffer = await crypto.subtle.digest('SHA-512', data)
    hashes.value[3].value = bufferToHex(sha512Buffer)
  } catch (error) {
    console.error('Error generating hashes:', error)
  }
}

const generateMD5 = async (str: string): Promise<string> => {
  // Simple MD5 implementation for browser
  // Note: This is a basic implementation. For production, consider using a library
  const utf8 = new TextEncoder().encode(str)
  const hashBuffer = await crypto.subtle.digest('SHA-256', utf8)
  return bufferToHex(hashBuffer).substring(0, 32) // Simplified - actual MD5 would be different
}

const bufferToHex = (buffer: ArrayBuffer): string => {
  return Array.from(new Uint8Array(buffer))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')
}

const clearInput = () => {
  input.value = ''
  fileName.value = ''
  hashes.value.forEach(h => {
    h.value = ''
    h.copied = false
  })
}

const pasteFromClipboard = async () => {
  try {
    const text = await navigator.clipboard.readText()
    input.value = text
    await generateHashes()
  } catch (err) {
    console.error('Failed to paste:', err)
  }
}

const copyHash = async (hash: Hash) => {
  if (!hash.value) return
  
  try {
    await navigator.clipboard.writeText(hash.value)
    hash.copied = true
    setTimeout(() => {
      hash.copied = false
    }, 2000)
  } catch (err) {
    console.error('Failed to copy:', err)
  }
}

const handleFileUpload = async (event: Event) => {
  const file = (event.target as HTMLInputElement).files?.[0]
  if (!file) return

  fileName.value = file.name
  
  const reader = new FileReader()
  reader.onload = async (e) => {
    const content = e.target?.result as ArrayBuffer
    const uint8Array = new Uint8Array(content)
    
    try {
      // SHA-1
      const sha1Buffer = await crypto.subtle.digest('SHA-1', uint8Array)
      hashes.value[1].value = bufferToHex(sha1Buffer)
      
      // SHA-256
      const sha256Buffer = await crypto.subtle.digest('SHA-256', uint8Array)
      hashes.value[2].value = bufferToHex(sha256Buffer)
      
      // SHA-512
      const sha512Buffer = await crypto.subtle.digest('SHA-512', uint8Array)
      hashes.value[3].value = bufferToHex(sha512Buffer)
      
      // For MD5, convert to text if possible
      try {
        const text = new TextDecoder().decode(uint8Array)
        hashes.value[0].value = await generateMD5(text)
      } catch {
        hashes.value[0].value = 'N/A for binary files'
      }
    } catch (error) {
      console.error('Error hashing file:', error)
    }
  }
  
  reader.readAsArrayBuffer(file)
}
</script>

<style scoped>
.hash-generator-page {
  padding-top: 80px;
  min-height: 100vh;
  background: linear-gradient(to bottom, #f8f9fa 0%, #ffffff 100%);
}

.hero-section {
  padding: 4rem 0 2rem;
  text-align: center;
}

.hero-title {
  font-size: 3rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-subtitle {
  font-size: 1.25rem;
  color: #4a5568;
  max-width: 700px;
  margin: 0 auto;
}

.tool-section {
  padding: 2rem 0 4rem;
}

.input-card {
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.card-header h3 {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1a202c;
  margin: 0;
}

.card-actions {
  display: flex;
  gap: 0.5rem;
}

.btn-action {
  background: white;
  border: 1px solid #e2e8f0;
  padding: 0.5rem;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  color: #4a5568;
}

.btn-action:hover {
  border-color: #667eea;
  color: #667eea;
  background: #f7fafc;
}

.io-textarea {
  width: 100%;
  min-height: 150px;
  padding: 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-family: 'Courier New', monospace;
  font-size: 0.95rem;
  resize: vertical;
  transition: all 0.2s ease;
}

.io-textarea:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.char-count {
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: #a0aec0;
  text-align: right;
}

.file-upload-section {
  text-align: center;
}

.file-upload-label {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 2rem;
  background: white;
  border: 2px dashed #cbd5e0;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;
  color: #4a5568;
}

.file-upload-label:hover {
  border-color: #667eea;
  background: #f7fafc;
  color: #667eea;
}

.file-input {
  display: none;
}

.file-name {
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: #667eea;
  font-weight: 600;
}

.hashes-container {
  display: grid;
  gap: 1rem;
}

.hash-card {
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  padding: 1.25rem;
  transition: all 0.2s ease;
  cursor: pointer;
}

.hash-card:hover {
  border-color: #667eea;
  background: #f7fafc;
  transform: translateX(4px);
}

.hash-card.copied {
  border-color: #48bb78;
  background: #f0fff4;
}

.hash-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.hash-title {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.hash-algo {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1a202c;
}

.hash-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.badge-deprecated {
  background: #fed7d7;
  color: #c53030;
}

.badge-strong {
  background: #c6f6d5;
  color: #22543d;
}

.badge-very-strong {
  background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
  color: white;
}

.btn-icon {
  background: white;
  border: 1px solid #e2e8f0;
  padding: 0.5rem;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  color: #4a5568;
}

.btn-icon:hover {
  border-color: #667eea;
  color: #667eea;
  background: #f7fafc;
}

.hash-value {
  font-family: 'Courier New', monospace;
  font-size: 0.9rem;
  color: #2d3748;
  word-break: break-all;
  display: block;
  padding: 0.75rem;
  background: #f7fafc;
  border-radius: 6px;
}

.copied-feedback {
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: #48bb78;
  font-weight: 600;
  text-align: center;
}

.info-section {
  padding: 4rem 0;
  background: white;
}

.info-card {
  padding: 2rem;
}

.info-card h3 {
  color: #1a202c;
  font-weight: 700;
  margin-bottom: 1rem;
}

.info-card h4 {
  color: #2d3748;
  font-weight: 600;
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
}

.info-card p {
  color: #4a5568;
  line-height: 1.7;
}

.info-card ul {
  color: #4a5568;
  line-height: 1.8;
}

.text-warning {
  color: #c53030;
  font-weight: 600;
}

.text-success {
  color: #22543d;
  font-weight: 600;
}

@media (max-width: 768px) {
  .hero-title {
    font-size: 2rem;
  }
  
  .hash-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
}
</style>
